/**
 * GstinVerifiedPanel — the green "✓ verified" card the B2B composer
 * renders after `trpc.onboarding.lookupGstin` resolves. Shows the
 * four fields GSTN returned (legal name, state, registration type,
 * PAN) so the user can sanity-check the lookup at a glance, plus a
 * `<details>` disclosure with the *optional* overrides — display
 * name, primary contact, phone, email.
 *
 * The override block is intentionally inline + collapsed. Operators
 * raising 3 invoices in 10 minutes (Priya, the speed-persona) never
 * open it — they paste GSTIN, glance at the summary, hit Save.
 * Founders setting up a new repeat customer with branded display
 * names + sales-rep contacts get the override surface without
 * leaving the page.
 *
 * Override state lives in the parent (Phase 1 = the route). This
 * component is a thin presenter — no internal mutation.
 */
import * as React from "react";
import { Check } from "lucide-react";
import type { z } from "zod";
import { onboarding as onboardingContract } from "@flint/api-contract";
import { cn } from "@flint/ui";

export type GstinLookupResult = z.infer<typeof onboardingContract.lookupGstinOutput>;

export interface CustomerOverrides {
  displayName?: string;
  contactName?: string;
  phone?: string;
  email?: string;
}

export interface GstinVerifiedPanelProps {
  /** GSTN lookup result. Drives the summary grid. */
  result: GstinLookupResult;
  /** Current override values. Kept in the parent. */
  overrides: CustomerOverrides;
  /** Patch callback — passes only the keys that changed. */
  onOverridesChange: (patch: CustomerOverrides) => void;
  /** When `true` the disclosure starts open. Default `false`. */
  startExpanded?: boolean;
  className?: string;
}

export function GstinVerifiedPanel({
  result,
  overrides,
  onOverridesChange,
  startExpanded = false,
  className,
}: GstinVerifiedPanelProps) {
  return (
    <div
      className={cn(
        "rounded-md border border-ok p-4",
        "bg-gradient-to-b from-ok-50/60 via-paper to-paper",
        className,
      )}
    >
      <header className="flex items-center gap-2.5 mb-3">
        <span aria-hidden className="grid place-items-center w-6 h-6 rounded-full bg-ok text-paper">
          <Check strokeWidth={3} className="w-3.5 h-3.5" />
        </span>
        <div className="flex flex-col">
          <strong className="text-sm font-semibold text-ink leading-tight">
            {result.tradeName ?? result.legalName}
          </strong>
          <span className="text-xs text-muted leading-tight">
            Verified against GSTN · just now
          </span>
        </div>
      </header>

      <dl className="grid grid-cols-2 gap-y-2.5 gap-x-4 mb-0">
        <Cell label="Legal name" value={result.legalName} />
        <Cell
          label="State of supply"
          value={`${stateLabel(result.state)} (${stateCode(result.state)})`}
        />
        <Cell
          label="Registration"
          value={`${result.type} · ${result.registeredOn ? `active since ${formatRegMonth(result.registeredOn)}` : "active"}`}
        />
        <Cell label="PAN" value={result.pan} mono />
      </dl>

      <details
        className="mt-3.5 pt-3 border-t border-dashed border-line group"
        {...(startExpanded ? { open: true } : {})}
      >
        <summary
          className={cn(
            "cursor-pointer select-none list-none",
            "inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700",
            'before:content-[""] before:w-0 before:h-0 before:transition-transform before:duration-100',
            "before:border-l-[5px] before:border-l-current",
            "before:border-y-[4px] before:border-y-transparent",
            "group-open:before:rotate-90 group-open:mb-3",
          )}
        >
          Override or add display name, contact, address
        </summary>

        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <OverrideField
              label="Display name"
              hint="Short label shown in lists and dropdowns. Defaults to legal name."
              optional
              value={overrides.displayName ?? ""}
              placeholder={result.tradeName ?? "e.g. Acme Trading"}
              onChange={(v) => onOverridesChange({ displayName: v })}
            />
            <OverrideField
              label="Primary contact"
              optional
              value={overrides.contactName ?? ""}
              placeholder="e.g. Rajiv Mehta"
              onChange={(v) => onOverridesChange({ contactName: v })}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <OverrideField
              label="WhatsApp number"
              optionalSub="for invoice delivery"
              value={overrides.phone ?? ""}
              placeholder="+91 98765 …"
              inputMode="tel"
              autoComplete="tel"
              onChange={(v) => onOverridesChange({ phone: v })}
            />
            <OverrideField
              label="Email"
              optionalSub="fallback"
              value={overrides.email ?? ""}
              placeholder="rajiv@example.in"
              inputMode="email"
              autoComplete="email"
              onChange={(v) => onOverridesChange({ email: v })}
            />
          </div>
        </div>
      </details>
    </div>
  );
}

function Cell({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-px min-w-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd
        className={cn(
          "text-xs font-medium text-ink-2 m-0",
          mono && "font-mono tabular-nums -tracking-[0.01em]",
        )}
      >
        {value}
      </dd>
    </div>
  );
}

interface OverrideFieldProps {
  label: string;
  hint?: string;
  optional?: boolean;
  optionalSub?: string;
  value: string;
  placeholder: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  autoComplete?: string;
  onChange: (next: string) => void;
}

function OverrideField({
  label,
  hint,
  optional,
  optionalSub,
  value,
  placeholder,
  inputMode,
  autoComplete,
  onChange,
}: OverrideFieldProps) {
  return (
    <label className="flex flex-col gap-1 min-w-0">
      <span className="text-xs font-semibold text-ink-2">
        {label}
        {optional ? <span className="text-muted font-medium ml-1">(optional)</span> : null}
        {optionalSub ? <span className="text-muted font-medium ml-1">({optionalSub})</span> : null}
      </span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        inputMode={inputMode}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-9 px-3 rounded-md border border-line bg-paper text-sm text-ink",
          "placeholder:text-muted",
          "hover:border-line-2",
          "focus:outline-none focus:border-sky-600 focus:ring-[3px] focus:ring-sky-100",
        )}
      />
      {hint ? <span className="text-xs text-muted leading-snug">{hint}</span> : null}
    </label>
  );
}

/* ── State-code helpers ─────────────────────────────────────── */
/* indianState is a 2-letter / state-name combo in the contract.
   In practice the contract returns the canonical state name (e.g.
   "Maharashtra"). The code mapping below covers the common ones
   the UI surfaces; unknowns fall back to `""`. */

const STATE_CODES: Record<string, string> = {
  Maharashtra: "27",
  Karnataka: "29",
  Delhi: "07",
  "Tamil Nadu": "33",
  Gujarat: "24",
  "Uttar Pradesh": "09",
  "West Bengal": "19",
  Telangana: "36",
  Haryana: "06",
  Punjab: "03",
  Rajasthan: "08",
  Kerala: "32",
  "Madhya Pradesh": "23",
  "Andhra Pradesh": "37",
  Bihar: "10",
  Odisha: "21",
  Jharkhand: "20",
  Assam: "18",
  Chhattisgarh: "22",
  Uttarakhand: "05",
  "Himachal Pradesh": "02",
  Goa: "30",
  Tripura: "16",
};

function stateLabel(state: string): string {
  return state;
}

function stateCode(state: string): string {
  return STATE_CODES[state] ?? "—";
}

function formatRegMonth(iso: string): string {
  // `iso` is the contract's `isoDate` — `YYYY-MM-DD`.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", { month: "short", year: "numeric" });
}
